import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_APTITUDE_TOPICS_API = process.env.REACT_APP_ADMIN_APTITUDE_TOPICS_API || `${API_BASE_URL}/api/admin-app/aptitude-topics/`;

const AdminAptitudeTopicsPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        name: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);

    const headers = useMemo(() => {
        const token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const detailUrl = (id) => `${ADMIN_APTITUDE_TOPICS_API}${id}/`;

    const resetForm = () => {
        setForm({ name: '' });
        setEditingId(null);
        setIsFormOpen(false);
    };

    const openAddTopicForm = () => {
        setEditingId(null);
        setForm({ name: '' });
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    const fetchRows = async () => {
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ADMIN_APTITUDE_TOPICS_API, { headers });
            const data = response.ok ? await response.json() : [];
            setRows(Array.isArray(data) ? data : []);
        } catch (error) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errorMessage) {
            setErrorMessage('');
        }
        if (successMessage) {
            setSuccessMessage('');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const topicName = form.name.trim();

        if (!topicName) {
            setErrorMessage('Topic name is required.');
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                name: topicName,
            };

            const response = await fetch(editingId ? detailUrl(editingId) : ADMIN_APTITUDE_TOPICS_API, {
                method: editingId ? 'PATCH' : 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const firstError = data?.error || data?.name?.[0] || 'Unable to save topic entry.';
                setErrorMessage(firstError);
                return;
            }

            setSuccessMessage(editingId ? 'Topic updated successfully.' : 'New topic added successfully.');
            resetForm();
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (item) => {
        setEditingId(item.id);
        setForm({
            name: item.name || '',
        });
        setErrorMessage('');
        setSuccessMessage('Editing selected topic.');
        setIsFormOpen(true);
    };

    const handleDelete = async (itemId) => {
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete topic?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(detailUrl(itemId), {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setErrorMessage(data?.error || 'Unable to delete topic.');
                return;
            }

            if (editingId === itemId) {
                resetForm();
            }

            setSuccessMessage('Topic deleted successfully.');
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            {isFormOpen ? (
                <div className="admin-top-sheet-backdrop" onClick={resetForm}>
                    <form
                        className="admin-college-form admin-top-sheet"
                        onSubmit={handleSubmit}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="admin-top-sheet-header">
                            <h4 className="admin-college-dropdown-title">{editingId ? 'Edit Topic' : 'Add New Topic'}</h4>
                            <button
                                type="button"
                                className="admin-top-sheet-close"
                                onClick={resetForm}
                                disabled={submitting}
                                aria-label="Close add topic form"
                            >
                                x
                            </button>
                        </div>

                        <div className="admin-college-form-grid">
                            <div>
                                <label className="admin-security-label" htmlFor="name">Topic Name</label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.name}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="admin-college-actions">
                            <button type="submit" className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn" disabled={submitting}>
                                {submitting ? 'Saving...' : editingId ? 'Update Topic' : 'Add Topic'}
                            </button>
                            <button
                                type="button"
                                className="admin-profile-action-btn"
                                onClick={resetForm}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="admin-college-header">
                <div>
                    <h3>Aptitude Topics</h3>
                    <p className="admin-subtitle">Manage aptitude topics with add, edit, and delete options.</p>
                </div>
                <button
                    type="button"
                    className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                    onClick={openAddTopicForm}
                    disabled={submitting}
                >
                    Add Topic
                </button>
            </div>

            {errorMessage ? <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div> : null}
            {successMessage ? <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div> : null}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Topic Name</th>
                            <th>Updated</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr>
                                <td colSpan="4">Loading topics...</td>
                            </tr>
                        ) : rows.length === 0 ? (
                            <tr>
                                <td colSpan="4">No topic records found.</td>
                            </tr>
                        ) : (
                            rows.map((item, index) => (
                                <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>{item.name || '-'}</td>
                                    <td>{item.updated_at ? new Date(item.updated_at).toLocaleString() : '-'}</td>
                                    <td>
                                        <div className="admin-inline-actions">
                                            <button
                                                type="button"
                                                className="admin-profile-action-btn"
                                                onClick={() => handleEdit(item)}
                                                disabled={submitting}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-profile-action-btn admin-profile-action-btn-danger"
                                                onClick={() => handleDelete(item.id)}
                                                disabled={submitting}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminAptitudeTopicsPage;
